"use client";

import React from "react";
import Input, { Textarea } from "@/components/ui/input/input";
import ToggleSwitch from "@/components/ui/input/toggleSwitch";
import { useFormContext } from "react-hook-form";
import { TokenFormData } from "@/lib/builderFormSchema";
import CustomDateTimePicker from "../../input/datePicker/CustomDateTimePicker";
import { IconBan, IconSnowflake } from "@tabler/icons-react";

const authorityOptions = [
  {
    icon: <IconSnowflake stroke={2} />,
    title: "Revoke freeze",
    description:
      "Freeze Authority allows you to freeze token accounts of holders",
  },
  {
    icon: <IconBan stroke={2} />,
    title: "Revoke Mint",
    description: "Mint Authority allows you to mint more supply of your token",
  },
  {
    icon: <IconSnowflake stroke={2} />,
    title: "Revoke update",
    description: "Update Authority allows you to update token metadata",
  },
];

const StepThree = () => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<TokenFormData>();

  const enableCustomLaunch = watch("enableCustomLaunch");
  const enableCreatorMod = watch("enableCreatorMod");
  const launchDate = watch("launchDate");

  return (
    <div className="space-y-6">
      <h2 className="text-white text-xl font-semibold mb-2">
        3- Advanced Settings
      </h2>
      <p className="text-muted mb-6 text-offwhite">
        Fine-tune your token's settings and allow for post-launch modifications.
        Manage the creator's information and revoke options with ease.
      </p>

      {/* Toggles */}
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-3">
            <ToggleSwitch
              checked={enableCustomLaunch || false}
              onChange={(e) =>
                setValue("enableCustomLaunch", e.target.checked, {
                  shouldValidate: true,
                })
              }
            />
            <p className="text-white">Enable custom launch time</p>
          </div>
          <p className="text-muted text-xs text-offwhite mt-2">
            Toggle this option to set a specific launch time for your token
          </p>
          <div className="bg-foreground border border-primaryborder p-3 rounded-lg my-2">
            <p className="text-muted text-xs text-offwhite mt-1">
              <strong>Note:</strong> If disabled, the public launch will
              automatically start 10 minutes after the presale ends.
            </p>
          </div>
        </div>

        {enableCustomLaunch && (
          <div className="p-4 bg-input-bgcolor rounded-xl border border-primaryborder">
            <label className="block text-white text-sm font-medium mb-2">
              Launch Date & Time
            </label>
            <CustomDateTimePicker
              date={launchDate ?? null}
              onChange={(date) =>
                setValue("launchDate", date, { shouldValidate: true })
              }
              error={errors.launchDate?.message}
              minDate={new Date()}
              placeholder="Select launch date & time"
            />
          </div>
        )}

        {/* Presale configuration - always visible */}
        <div className="space-y-4 p-4 bg-input-bgcolor rounded-xl border border-primaryborder">
          <h3 className="text-white font-medium">Presale Configuration</h3>
          <p className="text-muted text-sm text-offwhite">
            Set specific times for your presale period. Leave empty to use
            default timing (immediate launch).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Presale Start Time
              </label>
              <CustomDateTimePicker
                date={watch("presaleStartTime") ?? null}
                onChange={(date) =>
                  setValue("presaleStartTime", date, { shouldValidate: true })
                }
                error={errors.presaleStartTime?.message}
                minDate={new Date()}
                placeholder="Select presale start time"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Presale End Time
              </label>
              <CustomDateTimePicker
                date={watch("presaleEndTime") ?? null}
                onChange={(date) =>
                  setValue("presaleEndTime", date, { shouldValidate: true })
                }
                error={errors.presaleEndTime?.message}
                minDate={new Date()}
                placeholder="Select presale end time"
              />
            </div>
          </div>

          <div className="bg-background p-3 rounded-lg">
            <p className="text-xs text-offwhite">
              <strong>Timeline:</strong> Presale Start → Presale End → Main
              Token Launch. Ensure the presale ends before your main launch
              time.
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3 mt-6">
            <ToggleSwitch
              checked={enableCreatorMod || false}
              onChange={(e) =>
                setValue("enableCreatorMod", e.target.checked, {
                  shouldValidate: true,
                })
              }
            />
            <div>
              <p className="text-white">
                Enable modification of creator information{" "}
                <span className="text-muted text-sm text-offwhite">
                  (0.1 SOL)
                </span>
              </p>
            </div>
          </div>
          <p className="text-muted text-xs text-offwhite mt-2">
            Toggle this option to allow changes to the creator's details after
            launch.
          </p>
        </div>
      </div>

      {/* Creator Info */}
      <Input
        label="Creator name"
        placeholder="Enter creator name"
        {...register("creatorName")}
        error={errors.creatorName?.message}
      />

      <Input
        label="Creator site"
        placeholder="Enter creator site"
        {...register("creatorSite")}
        error={errors.creatorSite?.message}
      />

      <Textarea
        label="Post-Launch Marketing Plan"
        placeholder="Describe your marketing plan after launch"
        rows={5}
        {...register("marketingPlan")}
        error={errors.marketingPlan?.message}
      />

      {/* Security Standards */}
      <div className="pt-6 border-t-2 border-primaryborder">
        <label className="text-white font-medium mb-2 block">
          Security Standards
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {authorityOptions.map((item, idx) => (
            <div
              key={idx}
              className="bg-input-bgcolor p-4 rounded-xl border border-primaryborder"
            >
              <div className="flex justify-between">
                <p className="text-white font-medium flex gap-2 mb-3">
                  {item.icon} {item.title}
                </p>
                <p className="text-xs text-muted mt-2 text-offwhite">
                  +0.1 SOL
                </p>
              </div>
              <p className="text-muted text-sm mt-1 text-offwhite">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StepThree;
